// A stored figure (°F, ppt) in the keeper's own units, for copy outside the
// parameter cards: the Score sheet's livestock warnings, Ask's supporting
// records, a follow-up's before / latest, the quick-add label and the card
// pill's tooltip. Storage stays canonical; only what is shown converts.
import { formatSalinity, formatSalinityRange, salinityUnitLabel, sgDecimals } from './salinity.js';
import { cToF, fToC, formatTemp, formatVolume, galToL, lToGal } from './units.js';

// A row saved with some other unit (a TDS "ppm" on salinity) is not ours to
// convert; only the storage unit, or none, is.
function isStored(key, unit) {
  const u = String(unit ?? '').trim();
  if (key === 'temp') return !u || /^°?\s*f$/i.test(u);
  if (key === 'salinity') return !u || u.toLowerCase() === 'ppt';
  return false;
}

// "25.6°C", "1.026 S.G.", "8.2 dKH". Anything not in storage units is
// shown as it was saved.
export function shownFigure(key, value, unit, { salinityUnit = 'ppt', tempUnit = 'F' } = {}) {
  const n = Number(value);
  if (value == null || value === '' || !Number.isFinite(n)) {
    return `${value ?? ''}${unit ? ` ${unit}` : ''}`.trim();
  }
  if (isStored(key, unit) && key === 'temp') {
    return formatTemp(n, tempUnit === 'C' ? 'C' : 'F');
  }
  if (isStored(key, unit) && key === 'salinity') {
    return `${formatSalinity(n, salinityUnit)} ${salinityUnitLabel(salinityUnit)}`;
  }
  return `${value}${unit ? ` ${unit}` : ''}`;
}

// The Score sheet's "Watch for your livestock" numbers. The species ranges
// are kept in °F (care_params.temp_f), so a Celsius keeper read "Reading
// 76 °F; wants at least 77 °F" beside cards in °C.
export function speciesAlertFigures(alert, { tempUnit = 'F' } = {}) {
  const range = Array.isArray(alert?.range) ? alert.range : [];
  if (alert?.param === 'temp' && tempUnit === 'C' && isStored('temp', alert.unit)) {
    const c = (v) => Number(formatTemp(v, 'C', { withUnit: false }));
    return { value: c(alert.value), range: range.map(c), unit: '°C' };
  }
  return { value: alert?.value, range, unit: alert?.unit || '' };
}

// The unit beside the Home quick-add box. The save converts a typed
// temperature from °C on a Celsius tank (not a Hanna read), so the label
// says °C there; it said °F while the number was taken as Celsius.
export function quickAddUnitLabel({ param, unit, inputUnit, phosphateAs, method, tempUnit }) {
  if (param === 'salinity') return salinityUnitLabel(inputUnit);
  if (param === 'phosphate' && phosphateAs === 'p') return 'ppb';
  if (param === 'temp' && tempUnit === 'C' && method !== 'hanna') return '°C';
  return unit || '';
}

// The Home card pill's tooltip for OK / HIGH / LOW, in the units the card
// prints: it read "In acceptable range 77.5–78.5 °F" on a °C card.
export function pillRangeTitle(
  pill,
  key,
  band,
  unit,
  { salinityUnit = 'ppt', tempUnit = 'F' } = {}
) {
  const [lo, hi] = Array.isArray(band) ? band : [];
  const c = key === 'temp' && tempUnit === 'C';
  const d = key === 'salinity' && salinityUnit === 'sg' ? sgDecimals([lo, hi]) : undefined;
  const edge = (v) =>
    key === 'salinity'
      ? `${formatSalinity(v, salinityUnit, d)} ${salinityUnitLabel(salinityUnit)}`
      : c
        ? formatTemp(v, 'C')
        : `${v} ${unit || ''}`.trim();
  if (pill === 'OK') {
    const range =
      key === 'salinity'
        ? formatSalinityRange(lo, hi, salinityUnit, d)
        : c
          ? `${formatTemp(lo, 'C', { withUnit: false })}–${formatTemp(hi, 'C')}`
          : `${lo}–${hi} ${unit || ''}`.trim();
    return `In acceptable range ${range}`;
  }
  if (pill === 'HIGH') return `Above ${edge(hi)} — out of range`;
  if (pill === 'LOW') return `Below ${edge(lo)} — out of range`;
  return null;
}

// The tank profile line under Ask's supporting records: "200 L", not the
// stored "52.8 gallons".
export function profileVolumeText(gallons, volumeUnit) {
  const g = Number(gallons);
  if (gallons == null || gallons === '' || !Number.isFinite(g) || g <= 0) return 'volume unknown';
  return formatVolume(g, volumeUnit === 'L' ? 'L' : 'gal');
}

// Catalog sizes and temperatures are kept as whole gallons and whole °F.
// The custom species form and "Suggest a correction" asked a litres or °C
// keeper for "Min tank (gal)" and "Temp min (°F)"; they ask in the tank's
// units now and convert back to the stored ones.
export function catalogFieldLabel(base, kind, { volume = 'gal', temp = 'F' } = {}) {
  if (kind === 'volume') return `${base} (${volume === 'L' ? 'L' : 'gal'})`;
  if (kind === 'temp') return `${base} (°${temp === 'C' ? 'C' : 'F'})`;
  return base;
}

export function catalogValueShown(stored, kind, { volume = 'gal', temp = 'F' } = {}) {
  const n = Number(stored);
  if (stored == null || stored === '' || !Number.isFinite(n)) return stored;
  if (kind === 'volume' && volume === 'L') return Math.round(galToL(n));
  if (kind === 'temp' && temp === 'C') return Math.round(fToC(n));
  return n;
}

export function catalogValueStored(typed, kind, { volume = 'gal', temp = 'F' } = {}) {
  if (typed == null || String(typed).trim() === '') return null;
  const n = Number(typed);
  if (!Number.isFinite(n)) return null;
  if (kind === 'volume') return Math.round(volume === 'L' ? lToGal(n) : n);
  if (kind === 'temp') return Math.round(temp === 'C' ? cToF(n) : n);
  return n;
}
